import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { setChatlistOpenHover } from '../../../Store/features/chatlist/chatlistSlice';
import AssistentList from '../components/AssistentList';
import Input from '../../Utility/FormElements/Input';
import ChatListItem from '../ChatListItem/ChatListItem';
import useChatList from '../../../Hooks/useChatList';

const MIN_WIDTH = 300;
const MAX_WIDTH = 600;
const STORAGE_KEY = 'chatlist-width';

const getStoredWidth = (): number => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const parsed = parseInt(stored, 10);
            if (!isNaN(parsed) && parsed >= MIN_WIDTH && parsed <= MAX_WIDTH) {
                return parsed;
            }
        }
    } catch {
        // localStorage not available
    }
    return MIN_WIDTH;
};

export function ChatList() {
    const {
        ChatListActions,
        i18n,
        allChatsLoaded,
        chatlistOpen,
        chatlistOpenHover,
        ListItemEdit,
        setListItemEdit,
        filterValue,
        setFilter,
        themeLogo,
        chatsFetched,
        groupedChats,
        currentURL,
        dispatch,
    } = useChatList();

    const [width, setWidth] = useState(getStoredWidth);
    const [isResizing, setIsResizing] = useState(false);
    const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null);
    const wasResizingRef = useRef(false);

    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            setIsResizing(true);
            resizeRef.current = { startX: e.clientX, startWidth: width };
        },
        [width],
    );

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing || !resizeRef.current) return;
            const delta = e.clientX - resizeRef.current.startX;
            const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, resizeRef.current.startWidth + delta));
            setWidth(newWidth);
        };

        const handleMouseUp = () => {
            setIsResizing(false);
            resizeRef.current = null;
        };

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing]);

    useEffect(() => {
        if (wasResizingRef.current && !isResizing) {
            try {
                localStorage.setItem(STORAGE_KEY, String(width));
            } catch {
                // localStorage not available
            }
        }
        wasResizingRef.current = isResizing;
    }, [isResizing, width]);

    useEffect(() => {
        document.documentElement.style.setProperty('--chatlist-width', `${width}px`);
    }, [width]);

    const chatListItems = useMemo(() => {
        if (!groupedChats || Object.entries(groupedChats).length === 0) return null;

        return Object.entries(groupedChats).map(([headline, chatlistItems], i) => (
            <div className="gap-1 flex flex-col mb-4" key={i}>
                <p className="font-normal text-chatlist-timeSeparatorColor py-0 py-2 pl-1 m-0 sticky top-0 bg-chatlist-background z-10">
                    {headline}
                </p>
                {chatlistItems?.map((chatlistItem: any, j: any) => (
                    <ChatListItem
                        key={j}
                        setCanEdit={setListItemEdit}
                        editItem={ListItemEdit}
                        chatIdentifier={chatlistItem.identifier}
                        currentUrl={currentURL}
                        deleteChat={ChatListActions.deleteChatHandler}
                        chatTitle={chatlistItem.title || 'n/a'}
                        updateChatTitle={ChatListActions.updateTitle}
                        assistantTitle={chatlistItem.assistantTitle}
                        assistantImage={chatlistItem.assistantImage}
                    />
                ))}
            </div>
        ));
    }, [groupedChats, ListItemEdit, setListItemEdit, currentURL, ChatListActions]);

    const AssistentListMemo = useMemo(() => <AssistentList />, []);

    return (
        <div
            className={`relative h-[var(--app-height)] ${isResizing ? '' : 'transition-all duration-300'} ${
                chatlistOpen ? '' : 'w-0'
            }`}
            style={{ width: chatlistOpen ? `${width}px` : 0 }}
            onMouseLeave={() => !isResizing && dispatch(setChatlistOpenHover(false))}
        >
            <div
                className={`fixed z-[15] h-full overflow-x-hidden border-layout-borderColor ${isResizing ? '' : 'transition-all duration-300'} ${
                    chatlistOpenHover || chatlistOpen ? 'border-r' : 'w-[0px]'
                }`}
                style={{ width: chatlistOpenHover || chatlistOpen ? `${width}px` : 0 }}
            >
                <div className="absolute">
                    <div
                        className={`joyride-step-chat-list flex flex-col h-[var(--app-height)] pb-0 chat-list-container relative left-0 ${isResizing ? '' : 'transition-all duration-100'}`}
                        style={{ minWidth: `${width}px`, maxWidth: `${width}px` }}
                    >
                        <div className="bg-chatlist-background flex flex-col h-full">
                            <div className="w-full min-h-[100px] flex justify-center items-center">
                                <div className="flex items-center w-full h-full pl-[75px]">
                                    <button
                                        className="relative h-[50px] w-full text-xl font-semibold text-white"
                                        onClick={(e) => {
                                            ChatListActions.navigateToHome();
                                        }}
                                    >
                                        {themeLogo && (
                                            <img
                                                className="absolute m-auto h-auto max-w-[190px] max-h-[50px] pl-6 top-0 bottom-0 left-0 transition-all duration-300"
                                                src={themeLogo}
                                                alt={document.title}
                                            />
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div className="scroll-smooth overflow-x-hidden overflow-y-auto flex flex-col chat-list-list grow">
                                <div className="pt-3 px-6">
                                    <div className="joyride-step-navigation-userprompts w-full pb-0">
                                        <h5 className="font-normal text-layout-textcolor py-0 pl-1 mb-4">
                                            {i18n.translate({ id: 'chatlist.assistants', sourceName: 'Main' })}
                                        </h5>
                                        {AssistentListMemo}
                                    </div>

                                    <hr className="border-t border-layout-borderColor w-full my-6"></hr>

                                    <Input
                                        value={filterValue}
                                        onChange={(value) => setFilter(value)}
                                        placeholder={i18n.translate({ id: 'filter.placeholder', sourceName: 'Main' })}
                                        className="w-full"
                                    />
                                    <hr className="border-t border-layout-borderColor w-full my-6"></hr>
                                </div>

                                <div className={`scroll-smooth overflow-x-hidden overflow-y-auto px-6`}>
                                    {allChatsLoaded.length !== 0 ? (
                                        (chatListItems ?? (
                                            <p>
                                                {i18n.translate({
                                                    id: 'filter.notFound',
                                                    sourceName: 'ChatList',
                                                })}
                                            </p>
                                        ))
                                    ) : !chatsFetched ? (
                                        <div className="flex items-center justify-center w-full">
                                            <div className="relative flex items-center justify-center h-10 w-10">
                                                <div className="absolute inline-block w-10 h-10 border-4 border-t-primary border-gray-300 rounded-full animate-spin duration-[2s]"></div>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-center">
                                            {i18n.translate({
                                                id: 'noChats',
                                                sourceName: 'ChatList',
                                            })}
                                        </p>
                                    )}
                                </div>
                                <div className="sticky bottom-0 bg-chatlist-background px-6 pb-4 mt-auto">
                                    <hr className="border-t border-layout-borderColor w-full mt-0 mb-4"></hr>
                                    <p className="text-center text-chatlist-timeSeparatorColor text-sm m-0">
                                        &copy; {new Date().getFullYear()}{' '}
                                        <a href="https://www.varios-ai.com" target="_blank" rel="noopener noreferrer">
                                            VARIOS AI
                                        </a>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {chatlistOpen && (
                    <div
                        className={`absolute top-0 -right-1 w-2 h-full cursor-ew-resize z-20 group`}
                        onMouseDown={handleMouseDown}
                    >
                        <div
                            className={`absolute top-0 left-1/2 -translate-x-1/2 w-0.5 h-full transition-colors ${
                                isResizing ? 'bg-primary' : 'group-hover:bg-primary'
                            }`}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
